import { gsap } from 'gsap';

export class HeroNavAnimation {
  private nav: HTMLElement | null = null;
  private morphPill: HTMLElement | null = null;
  private links: HTMLElement[] = [];
  private linkContainer: HTMLElement | null = null;
  private pillTween: gsap.core.Tween | null = null;
  private entranceTween: gsap.core.Timeline | null = null;
  private reduceMotion: boolean = false;

  constructor() {
    this.nav = document.querySelector('.hero-nav');
    this.morphPill = document.querySelector('.hero-nav-morph');
    this.linkContainer = document.querySelector('.hero-nav-links');
    this.reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.nav) {
      if (this.reduceMotion) {
        gsap.set(this.nav, { clipPath: 'none', opacity: 1 });
      } else {
        this.setInitialState();
        this.playEntrance();
        this.setupMorphPill();
      }
    }
  }

  private setInitialState(): void {
    if (!this.nav) return;
    gsap.set(this.nav, {
      clipPath: 'inset(0 0 100% 0)',
      opacity: 0,
    });
    const logo = this.nav.querySelector('.hero-nav-logo');
    const linksWrap = this.nav.querySelector('.hero-nav-links');
    [logo, linksWrap].forEach((el) => {
      if (el) gsap.set(el, { opacity: 0, y: -12 });
    });
  }

  private playEntrance(): void {
    if (!this.nav) return;

    const logo = this.nav.querySelector('.hero-nav-logo');
    const linksWrap = this.nav.querySelector('.hero-nav-links');

    this.entranceTween = gsap.timeline({ defaults: { ease: 'power3.out' } })
      .to(this.nav, {
        clipPath: 'inset(0 0 0% 0)',
        opacity: 1,
        duration: 0.7,
      })
      .to(
        [logo, linksWrap],
        { opacity: 1, y: 0, duration: 0.4, stagger: 0.08 },
        '-=0.35'
      );
  }

  private setupMorphPill(): void {
    if (!this.morphPill || !this.linkContainer) return;

    this.links = Array.from(
      this.linkContainer.querySelectorAll<HTMLElement>('.hero-nav-link')
    );
    if (this.links.length === 0) return;

    gsap.set(this.morphPill, { left: 0, width: 0, opacity: 0 });

    this.links.forEach((link) => {
      link.addEventListener('mouseenter', () => this.morphToLink(link));
    });
    this.linkContainer.addEventListener('mouseleave', () => this.morphPillHide());
  }

  private morphToLink(link: HTMLElement): void {
    if (!this.morphPill) return;

    this.morphPill.classList.add('hero-nav-morph-visible');
    this.pillTween?.kill();
    this.pillTween = gsap.to(this.morphPill, {
      left: link.offsetLeft,
      width: link.offsetWidth,
      opacity: 1,
      duration: 0.4,
      ease: 'power2.out',
      overwrite: true,
    });
  }

  private morphPillHide(): void {
    if (!this.morphPill) return;
    this.pillTween?.kill();
    this.pillTween = gsap.to(this.morphPill, {
      opacity: 0,
      duration: 0.25,
      onComplete: () => {
        this.morphPill?.classList.remove('hero-nav-morph-visible');
      },
    });
  }

  public dispose(): void {
    this.entranceTween?.kill();
    this.pillTween?.kill();
    this.nav = null;
    this.morphPill = null;
    this.links = [];
    this.linkContainer = null;
  }
}
